// src/lib/ui.js
// Design system ADMIN / MÉNAGE / PROPRIO — Direction Artistique (DA, 2026-06-30).
// Valeurs faisant foi : docs/handoff/DESIGN.md §8 quater.
//
// Le RENDU PUBLIC VOYAGEUR (livret + briques, formulaires publics, paiement,
// parcours, services voyageur) est GELÉ sur la charte ADR-149 et vit dans
// src/lib/charte-voyageur.js (DSV). Ces composants importent DSV, JAMAIS ce DS.
//
// Anciennes clés (navy/sky/terra/green/red/amber/bg/white/textLight…) CONSERVÉES
// en bas, en alias des valeurs DA → le code admin existant ne casse pas ; les
// nouveaux écrans (fan-out) utilisent les noms canoniques (marine/ciel/corail/
// success/warning/danger/pageBg/cat*/src*…).

export const DS = {
  // ── MARQUE ──
  // ── CHARTE 2026 (décision Dimitri 2026-08-05) ──────────────────────────
  // « Ma charte c'est blanc, ciel #87b8e6, gris #3d5166. Je n'ai pas besoin de
  // 7 nuances. Si un endroit perd sa distinction, on la refera avec du blanc,
  // du ciel ou de la taille — pas avec un bleu de plus. »
  //
  // Les 4 jetons de marque convergent donc vers DEUX valeurs. Les noms
  // `marine*` sont conservés (1 439 usages) : les renommer serait un
  // refactor massif sans bénéfice. Ils ne désignent plus du marine.
  //
  // ⚠️ NE PAS réintroduire de nuance intermédiaire « pour retrouver la
  // profondeur ». C'est exactement ce qui a été refusé.
  marine:       "#3d5166",  // structure, texte fort, titres, montants
  marineDark:   "#2e3d4d",  // texte/titres sur fond clair
  marineDeep:   "#2e3d4d",  // texte sur fonds ciel — le plus foncé des deux
  marineHero:   "#3d5166",  // cartes "hero" pleines
  ciel:         "#87b8e6",  // COULEUR LOGO — signature : fonds doux, onglet actif, avatars
  cielSoft:     "#e4f0fb",  // fonds tintés ciel (pastilles, badges info)
  corail:       "#D85A30",  // signature : actions, CTA, moments clés, actif/survol
  corailHover:  "#c44e28",
  corailSoft:   "#FAECE7",  // fond doux corail
  corailText:   "#993C1D",  // texte sur fond corail doux

  // ── FONDS & SURFACES ──
  pageBg:       "#f6f6f7",  // fond de page (blanc pur grisé — JAMAIS de bleu-gris)
  card:         "#ffffff",  // cartes
  border:       "#e8e8ea",  // hairline
  borderHover:  "#cdd5df",
  toolBg:       "#edf1f5",  // panneau-OUTIL (aperçu/debug) — surface en retrait
  toolBorder:   "#dde4ec",
  toolTagBg:    "#dce8f5",  // tag "OUTIL"

  // ── TEINTES-CATÉGORIE (pastille d'icône de section — repère de nav FIXE, §2) ──
  // Repère de navigation. Suit la charte 2026 (arbitrage Dimitri : la SEULE
  // exception au remplacement est srcBooking, cf. plus bas).
  catCielBg:    "#e4f0fb",  catCielIco:   "#3d5166",  // ciel (contenu principal, signature)
  catIndigoBg:  "#EEEDFE",  catIndigoIco: "#534AB7",  // indigo (IA, secondaire distinct)
  catCorailBg:  "#FAECE7",  catCorailIco: "#993C1D",  // corail doux
  catArdoiseBg: "#eef1f4",  catArdoiseIco:"#5a6470",  // ardoise neutre = RÉGLAGES / config
  catRoseBg:    "#FBEAF0",  catRoseIco:   "#993556",  // rose (5e tint)

  // ── SÉMANTIQUE (états) ──
  success: "#1D9E75",  successBg: "#E1F5EE",  successText: "#0F6E56",  // payé, caution, fait
  warning: "#EF9F27",  warningBg: "#FAEEDA",  warningText: "#854F0B",  // désynchro, en attente
  danger:  "#E24B4A",  dangerBg:  "#FCEBEB",  dangerText:  "#A32D2D",  // erreur, suppression
  alertDot:"#ffd23f",  // marqueur d'état "fort" (jamais une couleur de source)

  // ── COULEURS DE SOURCE (réservations) ──
  srcAirbnb:  "#E8632E",
  // 🔴 SEULE EXCEPTION au passage à la charte 2026 (décision Dimitri
  // 2026-08-05) : « srcBooking reste marine, c'est la couleur de la marque
  // Booking dans mon calendrier. » C'est de la SÉMANTIQUE, pas de la charte —
  // un lecteur de calendrier reconnaît ses sources à la couleur.
  // C'est désormais le DERNIER #215387 légitime du design system.
  srcBooking: "#215387",
  srcDirect:  "#1D9E75",  // Direct ET sous-location interne FBZ — même vert
  srcInterne: "#1D9E75",  // alias → srcDirect (ancien violet abandonné)
  srcBloque:  "#b0b6c0",

  // ── TEXTE ──
  textPrimary:   "#22364a",
  textSecondary: "#5a6470",
  textMuted:     "#9295a0",

  // ── TYPO ──
  fontDisplay: "'Fraunces', serif",            // titres, chiffres clés, noms
  fontBody:    "'Libre Franklin', sans-serif", // corps, labels, boutons

  // ── RAYONS & MOUVEMENT (niveau 2) ──
  radiusControl: 9,   // boutons, champs
  radiusCard:    12,  // cartes
  durFast:       ".12s",  // clic (scale .96)
  durBase:       ".2s",   // survols, transitions

  // ════════════════════════════════════════════════════════════════════════
  // ALIAS RÉTRO-COMPAT — anciennes clés → valeurs DA. NE PAS retirer tant que
  // le fan-out écrans n'a pas migré le code vers les noms canoniques ci-dessus.
  // ════════════════════════════════════════════════════════════════════════
  // navyLight n'a plus de valeur propre : la charte ne porte que deux gris.
  // Une distinction « secondaire » se refait au blanc, au ciel ou à la taille.
  navy:     "#3d5166",  navyDark: "#2e3d4d",  navyLight: "#3d5166",
  sky:      "#87b8e6",  skyDeep:  "#0284c7",  skyPastel: "#a8d8f0",
  terra:    "#D85A30",  // ex-terracotta #C17B5C → corail (accent DA)
  green:    "#1D9E75",  // → success
  amber:    "#EF9F27",  // → warning
  red:      "#E24B4A",  // → danger
  charge:   "#e11d48",  chargePastel: "#fda4af",  // sémantique "charges" admin (hors DESIGN — conservée)
  bg:       "#f6f6f7",  // → pageBg
  white:    "#ffffff",  // → card
  textLight:"#9295a0",  // → textMuted
}

// Breakpoints responsive UNIFIÉS (décision Dimitri, chantier responsive Lot 0).
// Source partagée : les @media de App.jsx (fontsStyle) ET le hook useIsMobile
// s'alignent dessus. CSS « mobile » = max-width < md (767.98px).
export const BP = { sm: 480, md: 768, lg: 1100 }
